import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import InputComponent from "components/shared/InputComponent";
import SelectReact from "react-select";
import { getAllClasses } from "services/Teacher/Class.service";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { useParams } from "react-router-dom";
import { Assignments } from "services/Teacher/Assignments.service";
import { getClassTeachersAndStudents } from "services/Teacher/ClassesAndGroups.service";
import { getAllTeachersAndStudents } from "services/Teacher/User.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan, faClock } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import TextArea from "components/shared/TextArea";
import { getLibraryExerciseDetail } from "services/Admin/UserManagement/Exercise.service";
import useAutoSave from "hooks/useAutoSave";
import AutoSaveIndicator from "components/shared/AutoSaveIndicator";
import { getFormDraft, patchFormDraft, deleteFormDraft } from "services/formDraft.service";

const ExerciseInformationModal = ({ show, onHide, onAssigned, exerciseId, type = "exercise", variants = [], hasVocabulary = true, estimatedMinutes = null }) => {
  const { t } = useTranslation();
  const params = useParams();

  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  };

  const [formState, setFormState] = useState({
    name: "",
    startDate: new Date(),
    endDate: getDefaultEndDate(),
    maxTime: "",
    safeBrowser: false,
    forceSubmission: false,
    sources: [], // Array of sources
    allowMultipleAttempts: false, // Flag for allowing multiple attempts
    maxAttempts: 1, // Default maxAttempts
    includeVocabulary: false
  });
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]); // All available students
  const [selectedStudents, setSelectedStudents] = useState({});
  const [loading, setLoading] = useState(false);
  const [maxTimeEnabled, setMaxTimeEnabled] = useState(false);
  const [deadlineEnabled, setDeadlineEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const schoolId = JSON.parse(localStorage.getItem("user"))?.school;
  const [teacherInstructions, setTeacherInstructions] = useState('');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (show && schoolId) {
      fetchClassesAndGroups();
    }
  }, [show, schoolId]);

  // A new assignment has no server id until it is submitted, so in-progress work
  // is auto-saved to a per-exercise server draft and restored on reopen.
  const getDraftKey = () => `assignment-draft-${type}-${exerciseId || params.id}`;

  const clearDraft = () => {
    deleteFormDraft(getDraftKey()).catch(() => {});
  };

  // Restore a previously saved draft when the modal opens.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    // Re-gate autosave each time the modal (re)opens for an exercise, so a
    // debounced save can't overwrite the draft before it is restored.
    setHydrated(false);
    (async () => {
      try {
        const res = await getFormDraft(getDraftKey());
        const draft = res?.data?.result;
        if (
          !cancelled &&
          draft &&
          typeof draft === "object" &&
          Object.keys(draft).length
        ) {
          if (draft.formState && typeof draft.formState === "object") {
            const saved = draft.formState;
            // Dates round-trip through JSON as ISO strings; revive them so the
            // DatePicker and its minDate comparisons receive real Date objects.
            const reviveDate = (v) => (v ? new Date(v) : v);
            // Merge onto the current defaults so a key missing from an older
            // draft (e.g. a newly-added field) is not wiped to undefined.
            setFormState((prev) => ({
              ...prev,
              ...saved,
              startDate: reviveDate(saved.startDate) ?? prev.startDate,
              endDate: reviveDate(saved.endDate) ?? prev.endDate,
              sources: Array.isArray(saved.sources) ? saved.sources : prev.sources,
            }));

            // The multiselect renders from the separate `selectedStudents` map
            // (keyed by source index), NOT from formState.sources. Rebuild it
            // from the restored sources or the restored selections are invisible.
            if (Array.isArray(saved.sources)) {
              const rebuilt = {};
              saved.sources.forEach((source, index) => {
                rebuilt[index] = Array.isArray(source?.selectedStudents)
                  ? source.selectedStudents
                  : [];
              });
              setSelectedStudents(rebuilt);
            }
          }
          if (typeof draft.teacherInstructions === "string")
            setTeacherInstructions(draft.teacherInstructions);
          if (typeof draft.maxTimeEnabled === "boolean")
            setMaxTimeEnabled(draft.maxTimeEnabled);
          if (typeof draft.deadlineEnabled === "boolean")
            setDeadlineEnabled(draft.deadlineEnabled);
        }
      } catch (error) {
        /* ignore */
      } finally {
        // Enable autosave only after the draft has been applied (or confirmed
        // empty), so nothing overwrites the draft during the mount race.
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, exerciseId]);

  // Debounced draft auto-save: ~2.5s after the teacher stops editing, and at
  // least every 30s of continuous editing. Persists to the server form-draft
  // endpoint, PATCHing only the changed fields.
  const {
    status: autoSaveStatus,
    lastSavedAt: autoSaveAt,
    isOnline: autoSaveOnline,
    saveImmediately,
  } = useAutoSave(
    { formState, teacherInstructions, maxTimeEnabled, deadlineEnabled },
    (changed) => patchFormDraft(getDraftKey(), changed),
    {
      enabled: show && hydrated,
      canSave: () => Boolean(formState.name) || formState.sources.length > 0,
    }
  );

  const fetchClassesAndGroups = async () => {
    try {
      setLoading(true);
      const response = await getAllClasses(schoolId);
      setClasses(response.data.result.classes.data || []);
      setGroups(response.data.result.groups.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching classes/groups:", error);
    }
  };

  const fetchStudents = async (sourceId, index, sourceType) => {
    try {
      setLoading(true);

      let fetchedStudents = [];
      if (sourceType === "individual") {
        const response = await getAllTeachersAndStudents(schoolId, "student");
        fetchedStudents = response?.data.users || [];
      } else {
        const response = await getClassTeachersAndStudents(sourceId, "student", 1, 1000);
        fetchedStudents = response?.data.results.members || [];
      }

      const studentOptions = fetchedStudents.map((student) => ({
        value: student._id,
        label: student.fullName,
      }));

      setStudents(studentOptions);

      if (sourceType !== "individual") {
        setSelectedStudents((prev) => ({
          ...prev,
          [index]: studentOptions,
        }));

        const updatedSources = [...formState.sources];
        updatedSources[index].selectedStudents = studentOptions;
        setFormState((prevState) => ({
          ...prevState,
          sources: updatedSources,
        }));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching students:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Discrete-action helpers: update state AND flush an immediate save (spec:
  // "discrete actions save immediately"). Text inputs stay debounced.
  const commitFormState = (patch) => {
    const next = { ...formState, ...patch };
    setFormState(next);
    saveImmediately({ formState: next });
  };

  const commitField = (field, value) => commitFormState({ [field]: value });

  const handleStudentSelection = (index, selected) => {
    const updatedSources = [...formState.sources];
    updatedSources[index].selectedStudents = selected;
    commitField("sources", updatedSources);

    setSelectedStudents((prev) => ({
      ...prev,
      [index]: selected,
    }));
  };

  const handleSourceChange = (index, field, value) => {
    const updatedSources = [...formState.sources];
    updatedSources[index][field] = value;

    if (field === "sourceType") {
      updatedSources[index].sourceId = "";
      setSelectedStudents((prev) => ({
        ...prev,
        [index]: [],
      }));
      setStudents([]);
    }

    if (
      field === "sourceId" &&
      ["class", "group"].includes(updatedSources[index].sourceType)
    ) {
      fetchStudents(value, index, updatedSources[index].sourceType);
    } else if (field === "sourceType" && value === "individual") {
      fetchStudents(null, index, "individual");
    }

    commitField("sources", updatedSources);
  };

  const addSource = () => {
    setFormState((prevState) => ({
      ...prevState,
      sources: [
        ...prevState.sources,
        { sourceType: "class", sourceId: "", selectedStudents: [] },
      ],
    }));
  };

  const resetForm = () => {
    setFormState({
      name: "",
      startDate: new Date(),
      endDate: getDefaultEndDate(),
      maxTime: "",
      forceSubmission: false,
      safeBrowser: false,
      sources: [],
      allowMultipleAttempts: false,
      maxAttempts: 1,
      includeVocabulary: false,
    });
    setStudents([]);
    setSelectedStudents({});
    setClasses([]);
    setGroups([]);
    setMaxTimeEnabled(false);
    setDeadlineEnabled(true);
    setTeacherInstructions("");
  };

  const handleModalClose = () => {
    resetForm();
    onHide();
  };

  const removeSource = (index) => {
    setFormState((prevState) => ({
      ...prevState,
      sources: prevState.sources.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formState.name) errors.name = t("Title is required");
    if (!formState.startDate) errors.startDate = t("Start date is required");
    if (type === "exercise" && deadlineEnabled && !formState.endDate)
      errors.endDate = t("End date is required");

    if (formState.sources.length === 0) {
      errors.sources = t("At least one source is required");
    } else {
      const hasStudents = formState.sources.some(
        (source) =>
          source.selectedStudents && source.selectedStudents.length > 0
      );

      if (!hasStudents) {
        errors.students = t("At least one student must be assigned");
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      if (errors.sources) toast.error(errors.sources);
      if (errors.students) toast.error(errors.students);
      return;
    }

    setIsSubmitting(true);

    let maxAttemptsLimit = formState.maxAttempts;
    if (!formState.allowMultipleAttempts) {
      maxAttemptsLimit = -1;
    }

    const toastId = toast.loading(t("Assigning assignment..."));

    try {
      // Prepare the payload for the backend
      const payload = {
        title: formState.name,
        exercise: exerciseId || params.id,
        studySet: type === "studySet" ? exerciseId || params.id : null,
        type: type,
        startDate: formState.startDate,
        endDate: type === "studySet" || !deadlineEnabled ? null : formState.endDate,
        maxAttempts: type === "studySet" ? maxAttemptsLimit : null,
        maxTime: maxTimeEnabled ? formState.maxTime : null,
        forceSubmission: formState.forceSubmission,
        safeBrowser: formState.safeBrowser,
        includeVocabulary: formState.includeVocabulary,
        teacherInstructions: teacherInstructions,
        sources: formState.sources.map((source) => ({
          sourceType: source.sourceType === "group" ? "class" : source.sourceType,
          sourceId: source.sourceId,
          selectedStudents: source.selectedStudents.map((student) => student.value),
        })),
        // Add parent-child assignment specific fields
        isParentExercise: variants.length > 0,
        variants: variants.map(v => v._id) // Send variant IDs to backend
      };

      // Send single API call - backend will handle parent+child creation
      await Assignments(payload);

      toast.update(toastId, {
        render: t("Assignments assigned successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      clearDraft();
      resetForm();
      // A successful assign proceeds via onAssigned when provided; otherwise
      // fall back to the plain close behaviour.
      if (onAssigned) {
        onAssigned();
      } else {
        onHide();
      }
    } catch (error) {
      console.error(t("Error assigning assignment:"), error);
      toast.update(toastId, {
        render: t("Error assigning assignment"),
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSafeBrowserToggle = () => {
    commitField("safeBrowser", !formState.safeBrowser);
  };

  const handleIncludeVocabularyToggle = () => {
    commitField("includeVocabulary", !formState.includeVocabulary);
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      backdrop="static"
      centered
      size="lg"
      className="assign-modal"
    >
      <Modal.Header className="assign-modal-header" closeButton>
        <Modal.Title>
          {type === "studySet" ? t("Assign Studyset") : t("Assign Exercise")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="assign-modal-body"
        style={{ overflowY: "auto", maxHeight: "68vh" }}
      >
        {/* ── Assignment details ── */}
        <div className="assign-section">
          <h6 className="assign-section-title">{t("Assignment Details")}</h6>
          <InputComponent
            label={t("Assignment Title")}
            placeholder={t("Enter assignment title")}
            value={formState.name}
            setValue={(value) => handleInputChange("name", value)}
          />
          {type === "exercise" && estimatedMinutes != null && (
            <div className="assign-est-banner mt-3">
              <FontAwesomeIcon icon={faClock} />
              <span>
                {t("Estimated completion time")}:{" "}
                <strong>
                  {estimatedMinutes} {t("min")}
                </strong>
              </span>
            </div>
          )}
        </div>

        {/* ── Schedule ── */}
        <div className="assign-section">
            <h6 className="assign-section-title">{t("Schedule")}</h6>

            {type === "exercise" && (
              <Form.Check
                type="checkbox"
                className="mb-3"
                label={t("Set Deadline")}
                checked={deadlineEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDeadlineEnabled(checked);
                  if (checked && !formState.endDate) {
                    commitField("endDate", getDefaultEndDate());
                  }
                  saveImmediately({ deadlineEnabled: checked });
                }}
              />
            )}

            {(type !== "exercise" || deadlineEnabled) && (
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Label>{t("Start Date")}</Form.Label>
                  <DatePicker
                    selected={formState.startDate}
                    showTimeSelect={true}
                    minDate={new Date()}
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="time"
                    onChange={(date) => handleInputChange("startDate", date)}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="form-control"
                  />
                </Col>
                {type === "exercise" && (
                  <Col xs={12} md={6}>
                    <Form.Label>{t("End Date")}</Form.Label>
                    <DatePicker
                      selected={formState.endDate}
                      showTimeSelect={true}
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="time"
                      minDate={formState.startDate ? formState.startDate : new Date()}
                      onChange={(date) => handleInputChange("endDate", date)}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      className="form-control"
                    />
                  </Col>
                )}
              </Row>
            )}

            {type === "studySet" && (
              <div className="mt-3">
                <Form.Check
                  type="checkbox"
                  label={t("Allow Multiple Attempts")}
                  checked={formState.allowMultipleAttempts}
                  onChange={() =>
                    commitField(
                      "allowMultipleAttempts",
                      !formState.allowMultipleAttempts
                    )
                  }
                />
                {formState.allowMultipleAttempts && (
                  <div style={{ maxWidth: 260 }}>
                    <SelectReact
                      className="mt-3 exercise-select"
                      options={[
                        ...[1, 2, 3, 4, 5].map((value) => ({ value, label: value })),
                        { value: 1000, label: "Unlimited" }
                      ]}
                      value={{
                        value: formState.maxAttempts,
                        label: formState.maxAttempts === 1000 ? "Unlimited" : formState.maxAttempts
                      }}
                      onChange={(selected) => commitField("maxAttempts", selected.value)}
                      placeholder="Select Number of Attempts"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

        {/* ── Options ── */}
        {type === "exercise" && (
          <div className="assign-section">
            <h6 className="assign-section-title">{t("Options")}</h6>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Label>{t("Time Limit")}</Form.Label>
                <Form.Check
                  type="switch"
                  label={t("Enable Time Limit")}
                  checked={maxTimeEnabled}
                  onChange={(e) => {
                    setMaxTimeEnabled(e.target.checked);
                    saveImmediately({ maxTimeEnabled: e.target.checked });
                  }}
                />
                <InputComponent
                  className="pe-2 mt-2"
                  type="number"
                  placeholder={t("Enter time limit in minutes")}
                  value={formState.maxTime}
                  setValue={(value) => handleInputChange("maxTime", value)}
                  disabled={!maxTimeEnabled}
                />
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>{t("Safe Browser")}</Form.Label>
                <Form.Check
                  type="switch"
                  label={t("Enable Safe Browser")}
                  checked={formState.safeBrowser}
                  onChange={handleSafeBrowserToggle}
                />
              </Col>
            </Row>
          </div>
        )}

        {/* ── Assign to ── */}
        <div className="assign-section">
          <h6 className="assign-section-title">{t("Assign to")}</h6>
          {formState.sources.map((source, index) => (
            <div key={index} className="mb-3 ">
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Form.Check
                    type="radio"
                    label={t("Class")}
                    checked={source.sourceType === "class"}
                    onChange={() =>
                      handleSourceChange(index, "sourceType", "class")
                    }
                  />
                  <Form.Check
                    type="radio"
                    label={t("Group")}
                    checked={source.sourceType === "group"}
                    onChange={() =>
                      handleSourceChange(index, "sourceType", "group")
                    }
                  />
                  <Form.Check
                    type="radio"
                    label={t("Individuals")}
                    checked={source.sourceType === "individual"}
                    onChange={() =>
                      handleSourceChange(index, "sourceType", "individual")
                    }
                  />
                </div>
                <Button variant="danger" onClick={() => removeSource(index)}>
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </div>

              {(source.sourceType === "class" ||
                source.sourceType === "group") && (
                  <SelectReact
                    placeholder={t("Select Class/Group")}
                    isDisabled={loading}
                    className="mb-3 exercise-select"
                    options={
                      source.sourceType === "class"
                        ? classes.map((cls) => ({
                          value: cls._id,
                          label: cls.title,
                        }))
                        : groups.map((grp) => ({
                          value: grp._id,
                          label: grp.title,
                        }))
                    }
                    value={
                      (source.sourceType === "class"
                        ? classes.map((cls) => ({ value: cls._id, label: cls.title }))
                        : groups.map((grp) => ({ value: grp._id, label: grp.title }))
                      ).find((o) => o.value === source.sourceId) || null
                    }
                    onChange={(selected) =>
                      handleSourceChange(index, "sourceId", selected.value)
                    }
                  />
                )}
              <SelectReact
                isMulti
                placeholder={t("Select Students")}
                className="mb-3 exercise-select"
                isDisabled={loading}
                options={students}
                value={selectedStudents[index] || []}
                onChange={(selected) => handleStudentSelection(index, selected)}
              />
            </div>
          ))}
          <div className="mt-2">
            <Button variant="secondary" onClick={addSource}>
              {formState.sources.length === 0
                ? t("Add students")
                : t("Add more students")}
            </Button>
          </div>
          {type !== "studySet" && hasVocabulary && (
            <div className="mt-3">
              <Form.Check
                type="switch"
                label={t("Include Vocabulary")}
                checked={formState.includeVocabulary}
                onChange={handleIncludeVocabularyToggle}
              />
            </div>
          )}
        </div>

        {/* ── Teacher instructions ── */}
        <div className="assign-section">
          <h6 className="assign-section-title">{t("Teacher Instructions")}</h6>
          <TextArea
            type="text"
            placeholder={t("Write Exercise Instructions here")}
            className="w-100"
            style={{ height: "160px" }}
            value={teacherInstructions}
            onChange={(e) => setTeacherInstructions(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className="assign-modal-footer">
        <AutoSaveIndicator draft
          status={autoSaveStatus}
          lastSavedAt={autoSaveAt}
          isOnline={autoSaveOnline}
          className="me-auto"
        />
        <Button
          variant="secondary"
          className="button-style border-0 px-3"
          style={{
            background: "linear-gradient(270deg, #272727 0%, #4B4B4B 100%)",
          }}
          onClick={handleModalClose}
        >
          {t("Cancel")}
        </Button>
        <Button
          variant="primary"
          className="button-style border-0 px-3"
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
        >
          {t("Assign")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExerciseInformationModal;